import type { CartItem, CertificateType } from '../cart/types'
import type { SelectType } from 'application/ui/fields/select'
import { validateForm } from 'application/utils/form-validation'
import { CounterField } from 'application/ui/fields/counter-field'
import Modals from 'application/ui/modal/modal'
import { addToCart } from '../cart/_service'

/**
 * Класс для управления формами покупки сертификатов.
 * Обрабатывает отправку формы, отображение сообщений об успехе или ошибке, а также обновление количества товаров в корзине.
 */
export class CertificatePurchaseForm {
  private container: HTMLElement
  private form: HTMLFormElement
  private messageElement: HTMLElement | null = null
  private modals = Modals.getInstance()
  private modalBody: HTMLElement
  private certificateType: CertificateType
  private _modalName: string | null = null

  /**
   * Создает экземпляр формы покупки сертификата.
   * @param {HTMLElement} container - HTML-элемент контейнера, содержащий форму.
   */
  constructor(container: HTMLElement) {
    this.container = container

    this.init()
  }

  /**
   * Инициализирует форму: находит необходимые элементы, устанавливает обработчики событий
   * и настраивает валидацию ввода.
   */
  private init() {
    this.form = this.container.querySelector<HTMLFormElement>('form')!

    const modal = this.container.closest<HTMLElement>('[data-modal-name]')
    if (modal) {
      this.modalBody = modal.querySelector<HTMLElement>('.modal__body')!
      this._modalName = modal.dataset.modalName!
      this.modals.addModal(
        this._modalName,
        {
          closeCallback: () => this.onModalClose()
        },
        true
      )
    }

    const counters = this.form.querySelectorAll<HTMLElement>(
      '[data-counter-field]'
    )
    counters.forEach((counter) => {
      new CounterField(counter)
    })

    const typeInput = this.form.querySelector<HTMLInputElement>(
      'input[name="certificate_type"]'
    )
    this.certificateType = (typeInput?.value as CertificateType) || 'digital'

    this.form.addEventListener('submit', this.onSubmit)
  }

  /**
   * Имя модального окна, в котором открыта форма.
   */
  public get modalName(): string | null {
    return this._modalName
  }

  /**
   * Устанавливает номинал сертификата в поле выбора цены.
   * @param {number} price - Номинал сертификата.
   */
  public setPrice(price: number): void {
    const selectField = this.form.querySelector<SelectType>(
      '[data-field-name="price"] .select-field'
    )
    selectField?.select.setValue(String(price))
  }

  /**
   * Обработчик события отправки формы.
   * Предотвращает стандартное поведение формы, удаляет предыдущие сообщения,
   * выполняет валидацию и отправляет данные на сервер.
   * @param {Event} event - Событие отправки формы.
   */
  private onSubmit = (event: Event) => {
    event.preventDefault()

    if (this.messageElement) {
      this.messageElement.remove()
      this.messageElement = null
    }

    if (!validateForm(this.form)) {
      return
    }

    this.addToCart()
    this.renderSuccess()
  }

  private onModalClose = () => {
    setTimeout(() => {
      if (this.messageElement) {
        this.messageElement.remove()
        this.messageElement = null
      }

      this.form.reset()
      this.container.classList.remove('is-hidden')
    }, 600)
  }

  /**
   * Отображает сообщение об успешной отправке формы. В зависимости от типа сертификата отображает разные сообщения и ссылки.
   */
  private renderSuccess() {
    this.renderMessage({
      icon: 'double-ok',
      title: 'Сертификат добавлен в корзину'
    })
  }

  /**
   * Отображает сообщение с иконкой, заголовком и текстом.
   * Удаляет предыдущее сообщение, если оно существует.
   * @param {Object} params - Параметры сообщения.
   * @param {string} params.icon - ID иконки для отображения в SVG спрайте.
   * @param {string} [params.title] - Заголовок сообщения (опционально).
   * @param {string} [params.message] - Текст сообщения (опционально).
   * @param {Object} [params.link] - Объект с текстом и URL ссылки (опционально).
   * @param {string} params.link.text - Текст ссылки.
   * @param {string} params.link.url - URL для ссылки.
   */
  private renderMessage({
    icon,
    title,
    message,
    link
  }: {
    icon: string
    title?: string
    message?: string
    link?: { text: string; url: string }
  }) {
    if (this.messageElement) {
      this.messageElement.remove()
    }

    this.container.classList.add('is-hidden')

    this.messageElement = this.createMessageElement({
      icon,
      title: title || '',
      message: message || '',
      link
    })

    this.modalBody.appendChild(this.messageElement)
  }

  /**
   * Создает HTML-элемент сообщения с указанными параметрами.
   * @param {Object} params - Параметры для создания элемента.
   * @param {string} params.icon - ID иконки для отображения в SVG спрайте.
   * @param {string} params.title - Заголовок сообщения.
   * @param {string} params.message - Текст сообщения.
   * @param {{ text: string, url: string }} [params.link] - Объект с текстом и URL ссылки (опционально).
   * @return {HTMLElement} Созданный HTML-элемент с сообщением.
   */
  private createMessageElement({
    icon,
    title,
    message,
    link
  }: {
    icon: string
    title: string
    message: string
    link?: { text: string; url: string }
  }): HTMLElement {
    const container = document.createElement('div')
    container.classList.add('certificate-purchase-form__status')

    const titleTemplate = title
      ? `<b class="certificate-purchase-form__status-title">${title}</b>`
      : ''
    const messageTemplate = message
      ? `<p class="certificate-purchase-form__status-text">${message}</p>`
      : ''
    const linkTemplate = link
      ? `<a href="${link.url}" class="button button_view_action">${link.text}</a>`
      : ''

    container.innerHTML = `
      <span class="certificate-purchase-form__status-icon">
        <svg>
          <use href="#${icon}"></use>
        </svg>
      </span>
      ${titleTemplate}
      ${messageTemplate}
      ${linkTemplate}
    `
    return container
  }

  /**
   * Обновляет количество товаров в корзине, отображаемое на странице.
   * @param {number} count - Новое количество товаров в корзине.
   */
  private updateCartItemsCount(count: number) {
    const element = document.querySelector<HTMLElement>('[data-cart-count]')
    if (element) {
      element.dataset.cartCount = count.toString()
    }
  }

  /**
   * Добавляет выбранный сертификат в корзину. Собирает данные из формы, создает объект товара и вызывает функцию добавления в корзину. После добавления обновляет отображение количества товаров в корзине.
   */
  private addToCart() {
    const title =
      this.form.querySelector<HTMLInputElement>('input[name="title"]')?.value ||
      'Сертификат'

    const quantityInput = this.form.querySelector<HTMLInputElement>(
      'input[name="quantity"]'
    )
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1

    const priceInput = this.form.querySelector<HTMLInputElement>(
      'input[name="price"]'
    )
    const price = priceInput ? parseFloat(priceInput.value) : 0

    const colorInput = this.form.querySelector<HTMLInputElement>(
      'input[name="color"]'
    )

    const parameters: Record<string, string> = {}
    const color = colorInput ? colorInput.value : null
    if (color) {
      parameters.color = color
    }

    const imageInput = this.form.querySelector<HTMLInputElement>(
      'input[name="image"]'
    )
    const image = imageInput ? imageInput.value : ''

    const item: CartItem = {
      id: Date.now(),
      title,
      type: this.certificateType,
      quantity,
      price,
      parameters,
      currency:
        this.form.querySelector<HTMLInputElement>('input[name="currency"]')
          ?.value === 'BYN'
          ? 'BYN'
          : 'RUB',
      image
    }

    const cartCount = addToCart(item)
    this.updateCartItemsCount(cartCount)
  }
}
